/**
 * Authentication Service for Red Bridge Farm Feed Co.
 *
 * SECURITY: Uses httpOnly cookies for token storage (set by backend).
 * Tokens are NOT stored in localStorage to prevent XSS token theft.
 * All API calls use credentials: 'include' to send cookies.
 *
 * IMPORTANT: This is for INTERNAL portals (Admin, Mill, Store, Office).
 * Customer Portal uses separate customerAuthService.ts
 */

// Normalize API_BASE - remove trailing /api if present to avoid double /api/api
// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_RAW = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\.+\/*$/, '').replace(/\/+$/, '');
const API_BASE = API_BASE_RAW.replace(/\/api\/?$/, '');

// User data storage (non-sensitive, for UI display only)
const USER_DATA_KEY = 'rbf_user_data';

// Legacy keys to clean up on logout
const LEGACY_TOKEN_KEYS = [
  'rbf_access_token',
  'rbf_refresh_token',
  'access_token',
  'refresh_token',
];

// ===== TOKEN MANAGEMENT =====
// Tokens are now stored in httpOnly cookies by the backend.
// These functions are kept for backward compatibility but are no-ops.

export const setTokens = (_access: string, _refresh: string) => {
  // NO-OP: Tokens are stored in httpOnly cookies by backend
  // This function is kept for backward compatibility
  console.debug('[AUTH] Tokens stored in httpOnly cookies (not localStorage)');
};

export const getAccessToken = (): string | null => {
  // Tokens are in httpOnly cookies - frontend cannot read them
  // Return null; actual auth is via cookies with credentials: 'include'
  return null;
};

export const getRefreshToken = (): string | null => {
  // Tokens are in httpOnly cookies - frontend cannot read them
  return null;
};

export const clearTokens = () => {
  // Clear user data
  localStorage.removeItem(USER_DATA_KEY);
  // Clean up any legacy token storage
  LEGACY_TOKEN_KEYS.forEach(key => localStorage.removeItem(key));
};

// ===== USER DATA =====

export interface UserPermissions {
  admin_portal: boolean;
  mill_portal: boolean;
  store_portal: boolean;
  crm: boolean;
  crm_assignment: string;
  excluded_sections: string[];
  is_superuser: boolean;
}

export interface UserData {
  id: number;
  email: string;
  username?: string;
  name: string;
  first_name?: string;
  last_name?: string;
  is_staff: boolean;
  is_superuser: boolean;
  role?: string;
  portals: string[];
  has_passkeys?: boolean;
  has_crm_access?: boolean;
  permissions?: UserPermissions;
}

export const setUserData = (user: UserData) => {
  localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
};

export const getUserData = (): UserData | null => {
  try {
    const data = localStorage.getItem(USER_DATA_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('[AUTH] Failed to parse user data:', error);
    return null;
  }
};

// ===== TOKEN REFRESH =====

export const refreshAccessToken = async (): Promise<boolean> => {
  // Refresh token is in httpOnly cookie, backend reads it automatically
  // SECURITY: New tokens are set in httpOnly cookies only (not returned in response)
  try {
    const response = await fetch(`${API_BASE}/api/token/refresh/`, {
      method: 'POST',
      credentials: 'include',  // Send httpOnly cookies
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.ok) {
      // New tokens are set in httpOnly cookies by backend (not in response body)
      console.debug('[AUTH] Token refresh successful');
      return true;
    } else {
      clearTokens();
      return false;
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

// ===== AUTH CHECK =====

export const isAuthenticated = async (): Promise<boolean> => {
  // Check if we have user data cached
  const userData = getUserData();
  if (!userData) return false;

  // Verify with backend that session is still valid
  try {
    const response = await fetch(`${API_BASE}/api/auth/me/`, {
      credentials: 'include',  // Send httpOnly cookies
    });

    if (response.ok) {
      return true;
    } else if (response.status === 401) {
      // Try to refresh token
      const refreshed = await refreshAccessToken();
      return refreshed;
    }
  } catch {
    // Network error - assume still authenticated if we have user data
    return true;
  }

  return false;
};

/**
 * Synchronous check - just checks if user data exists locally
 * For actual auth verification, use isAuthenticated() which checks with backend
 */
export const hasValidToken = (): boolean => {
  // With httpOnly cookies, we can't check token validity directly
  // Just check if user data exists as a proxy
  return getUserData() !== null;
};

// ===== PORTAL ACCESS =====

export const canAccessPortal = (portal: 'admin' | 'mill' | 'store' | 'office'): boolean => {
  const userData = getUserData();
  if (!userData) return false;

  const perms = userData.permissions;
  if (perms) {
    if (perms.is_superuser) return true;
    const portalMap: Record<string, boolean> = {
      admin: perms.admin_portal,
      mill: perms.mill_portal,
      store: perms.store_portal,
      office: userData.is_staff,
    };
    return portalMap[portal] ?? false;
  }

  // Fallback for cached data without permissions object
  if (userData.is_superuser) return true;
  return (userData.portals || []).includes(portal);
};

export const getUserPortals = (): string[] => {
  const userData = getUserData();
  if (!userData) return [];

  if (userData.is_superuser) return ['admin', 'mill', 'store', 'office'];
  return userData.portals || [];
};

// ===== LOGIN =====

export interface LoginResult {
  success: boolean;
  error?: string;
  locked_minutes?: number;
}

export interface AccountOption {
  type: 'team' | 'portal' | 'bridge_club';
  label: string;
  subtitle: string;
}

export interface AvailablePortal {
  id: string;
  name: string;
  icon: string;
  url: string;
}

export interface UniversalLoginResult {
  success: boolean;
  auto_login?: boolean;  // true if single account (completed login), false if multiple accounts
  user_type?: 'team' | 'portal' | 'bridge_club';
  redirect_url?: string;
  error?: string;
  locked_minutes?: number;
  requires_verification?: boolean;
  show_portal_selector?: boolean;  // true if user has multiple portals
  user?: {
    id: number;
    email: string;
    name: string;
    is_staff: boolean;
    is_superuser: boolean;
    portals: string[];
    available_portals?: AvailablePortal[];
  };
  member?: any;
  // Multi-account support
  accounts?: AccountOption[];
  message?: string;
}

export const login = async (email: string, password: string): Promise<LoginResult> => {
  try {
    const response = await fetch(`${API_BASE}/api/token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',  // Required for httpOnly cookies to be set
      body: JSON.stringify({ email: email.toLowerCase().trim(), password }),
    });

    const data = await response.json();

    if (response.ok) {
      console.log('[AUTH] Login successful, cookies set by backend');

      // SECURITY: Tokens are in httpOnly cookies only (not in response body)
      // User data is returned directly from the backend
      const user = data.user;
      const userData: UserData = {
        id: user.id,
        email: user.email,
        username: user.username,
        name: user.name,
        first_name: user.first_name,
        last_name: user.last_name,
        is_staff: user.is_staff,
        is_superuser: user.is_superuser,
        role: user.role,
        portals: user.portals || [],
        has_crm_access: user.has_crm_access,
        permissions: user.permissions,
      };
      setUserData(userData);

      console.log('[AUTH] User data stored:', userData.email);
      return { success: true };
    } else if (response.status === 423) {
      // Account locked
      return { success: false, error: data.detail || data.error, locked_minutes: data.locked_minutes };
    } else if (response.status === 429) {
      // Rate limited
      return { success: false, error: 'Too many login attempts. Please wait a moment.' };
    } else {
      return { success: false, error: data.detail || data.error || 'Invalid email or password' };
    }
  } catch (error) {
    return { success: false, error: 'Network error. Please try again.' };
  }
};

/**
 * Universal login - tries all user types and redirects to correct portal.
 * Checks credentials against: Team/Staff → Portal → Bridge Club
 *
 * If the email exists in multiple systems (e.g., Bridge Club personal account
 * AND Portal employee account), returns auto_login=false with accounts[] for
 * the user to choose from.
 *
 * @param email - User's email address
 * @param password - User's password
 * @param accountType - Optional: if provided, completes login for that specific account type
 */
export const universalLogin = async (
  email: string,
  password: string,
  accountType?: 'team' | 'portal' | 'bridge_club'
): Promise<UniversalLoginResult> => {
  try {
    const body: any = {
      email: email.toLowerCase().trim(),
      password,
    };

    // If account type is specified, include it to complete login for that account
    if (accountType) {
      body.account_type = accountType;
    }

    const response = await fetch(`${API_BASE}/api/auth/universal-login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',  // Required for httpOnly cookies to be set
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (response.ok && data.success) {
      // Check if this is a multi-account response (user needs to choose)
      if (data.auto_login === false && data.accounts) {
        console.log('[AUTH] Multiple accounts found for:', email);
        return {
          success: true,
          auto_login: false,
          accounts: data.accounts,
          message: data.message,
        };
      }

      // Single account or account selection completed - login is done
      console.log('[AUTH] Universal login successful:', data.user_type);

      // Store user data based on user type
      if (data.user_type === 'team') {
        const user = data.user;
        const userData: UserData = {
          id: user.id,
          email: user.email,
          username: user.username,
          name: user.name,
          first_name: user.first_name,
          last_name: user.last_name,
          is_staff: user.is_staff,
          is_superuser: user.is_superuser,
          role: user.role,
          portals: user.portals || [],
          has_crm_access: user.has_crm_access,
          permissions: user.permissions,
        };
        setUserData(userData);
      } else if (data.user_type === 'portal') {
        // Store portal user data in localStorage for PortalAuthContext
        const userData = data.user;
        localStorage.setItem('portal_user', JSON.stringify(userData));
      } else if (data.user_type === 'bridge_club') {
        // Store bridge club member data in localStorage for BridgeClubAuthContext
        const memberData = data.member;
        localStorage.setItem('bridge_club_member', JSON.stringify(memberData));
      }

      return {
        success: true,
        auto_login: true,
        user_type: data.user_type,
        redirect_url: data.redirect_url,
        user: data.user,
        member: data.member,
      };
    } else if (response.status === 403 && data.requires_verification) {
      // Bridge Club member needs email verification
      return {
        success: false,
        user_type: 'bridge_club',
        requires_verification: true,
        error: data.error,
      };
    } else if (response.status === 423) {
      // Account locked
      return { success: false, error: data.error, locked_minutes: data.locked_minutes };
    } else if (response.status === 429) {
      // Rate limited
      return { success: false, error: 'Too many login attempts. Please wait a moment.' };
    } else {
      return { success: false, error: data.error || 'Invalid email or password' };
    }
  } catch (error) {
    console.error('[AUTH] Universal login error:', error);
    return { success: false, error: 'Network error. Please try again.' };
  }
};

// ===== LOGOUT =====

export const logout = async (redirectTo: string = '/login') => {
  // Always call logout endpoint to clear httpOnly cookies
  // credentials: 'include' is required for the Set-Cookie header to be processed
  try {
    await fetch(`${API_BASE}/api/auth/logout/`, {
      method: 'POST',
      credentials: 'include',  // Required for cookie clearing to work
      headers: {
        'Content-Type': 'application/json',
      },
      // Refresh token is in httpOnly cookie, backend will read it from there
    });
  } catch {
    // Ignore errors, still clear local data
  }

  clearTokens();
  window.location.href = redirectTo;
};

// ===== FETCH CURRENT USER =====

export const fetchCurrentUser = async (): Promise<UserData | null> => {
  try {
    const response = await fetch(`${API_BASE}/api/auth/me/`, {
      credentials: 'include',  // Send httpOnly cookies
    });

    if (response.ok) {
      const user = await response.json();
      setUserData(user);
      return user;
    }
  } catch {
    // Ignore
  }

  return null;
};

// ===== SECURITY ACTIVITY =====

export interface LoginActivity {
  attempted_at: string;
  successful: boolean;
  ip_address: string;
}

export interface AuditEntry {
  action: string;
  timestamp: string;
  details: Record<string, any>;
  ip_address: string;
}

export const getSecurityActivity = async (): Promise<{ recent_logins: LoginActivity[]; audit_log: AuditEntry[] } | null> => {
  try {
    const response = await fetch(`${API_BASE}/api/auth/security-activity/`, {
      credentials: 'include',  // Send httpOnly cookies
    });

    if (response.ok) {
      return await response.json();
    }
  } catch {
    // Ignore
  }

  return null;
};

// ===== CHANGE PASSWORD =====

export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/api/auth/change-password/`, {
      method: 'POST',
      credentials: 'include',  // Send httpOnly cookies
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true };
    } else {
      return { success: false, error: Array.isArray(data.error) ? data.error.join(' ') : data.error };
    }
  } catch {
    return { success: false, error: 'Network error' };
  }
};
